import {Inject, Injectable} from '@angular/core';
import {HttpClient, HttpResponse} from '@angular/common/http';
import {Observable, catchError, forkJoin, map, of, shareReplay, throwError} from 'rxjs';
import { User, RegistrationModel, Account, UserInvite, GridSettings, TeamMember, SidebarProjection, ResolvedUser, ResolvedUserStatus } from '@ee/common/models';
import {SESSION_ID} from '@ee/common/constants';

@Injectable({ providedIn: 'root' })
export class UserService {
  public resourceUrl: string;

  // Session-lifetime cache for /users/resolve lookups (historical assignees rarely change).
  private resolvedUserCache = new Map<string, Observable<ResolvedUser | null>>();

  constructor(
    private http: HttpClient,
    @Inject('environment') private environment
  ) {
    this.resourceUrl = this.environment.api_prefix + 'api/users';
  }

  /**
   * Resolves arbitrary user ids (historical assignees/watchers) to display info plus an
   * ACTIVE/DISABLED/REMOVED status. Unlike the suggestions list this also resolves deleted
   * (tombstoned) and org-unlinked users. Successful results are cached per id for the session;
   * ids the backend cannot resolve are omitted. A failed request is NOT cached — the failed ids
   * are evicted so a later call retries (otherwise one flaky request would poison those ids, and
   * every forkJoin batch containing them, for the whole session).
   */
  resolveUsers(ids: string[]): Observable<ResolvedUser[]> {
    const distinct = [...new Set(ids.filter(id => !!id))];
    if (!distinct.length) {
      return of([]);
    }
    const missing = distinct.filter(id => !this.resolvedUserCache.has(id));
    if (missing.length) {
      const request = this.http.post<ResolvedUser[]>(`${this.resourceUrl}/resolve`, { user_ids: missing }).pipe(
        // Display model: unify the org-relative status into the styling flags so an org-unlinked
        // user (status REMOVED, but not tombstoned) greys out exactly like a deleted one, and
        // guarantee a non-empty display_name for avatars/tooltips.
        map(users => users.map(u => ({
          ...u,
          display_name: u.display_name || [u.first_name, u.last_name].filter(Boolean).join(' ') || 'Unknown user',
          disabled: u.disabled || u.status === ResolvedUserStatus.DISABLED,
          deleted: u.deleted || u.status === ResolvedUserStatus.REMOVED
        }))),
        catchError(err => {
          missing.forEach(id => this.resolvedUserCache.delete(id));
          return throwError(() => err);
        }),
        shareReplay(1)
      );
      missing.forEach(id => {
        this.resolvedUserCache.set(id, request.pipe(map(users => users.find(u => u.id === id) ?? null)));
      });
    }
    return forkJoin(distinct.map(id => this.resolvedUserCache.get(id) ?? of(null))).pipe(
      map(users => users.filter((u): u is ResolvedUser => !!u))
    );
  }

  getLoggedInUser(forceRefresh = true, includeDashboard = true): Observable<User> {
    const params: string[] = [];
    if (includeDashboard) {
      params.push('include-dashboard=true');
    }
    if (forceRefresh) {
      params.push('force-refresh=true');
    }
    const queryParams = params.length > 0 ? `?${params.join('&')}` : '';
    return this.http.get<User>(`${this.environment.api_prefix}api/users/current${queryParams}`);
  }

  register(registrationModel: RegistrationModel): Observable<HttpResponse<any>> {
    return this.http.post(`${this.environment.api_prefix}api/register`, registrationModel, { observe: 'response' });
  }

  getUserSuggestions(attorneyUsers: boolean = false): Observable<User[]> {
    const params = {};
    if (attorneyUsers) {
      params['attorney-users'] = true;
    }
    return this.http.get<User[]>(`${this.environment.api_prefix}api/users/suggestions`, { params });
  }

  getTeamMembers(): Observable<TeamMember[]> {
    return this.http.get<TeamMember[]>(`${this.resourceUrl}/team-members`);
  }

  getUsersByOrganization(orgId: string, simple = false): Observable<User[]> {
    const params = {};
    if (simple) {
      params['simple'] = true;
    }
    return this.http.get<User[]>(`${this.environment.api_prefix}api/organizations/${orgId}/users`, { params });
  }

  checkEmailAvailability(
    emailAddress: string,
    limitToCurrentOrganization = false,
    clientId: string = null
  ): Observable<boolean> {
    let queryParams = `?login=${encodeURIComponent(emailAddress)}`;
    if (clientId) {
      queryParams += `&client-id=${clientId}`;
    }
    return this.http.get<boolean>(
      `${this.resourceUrl}/check-availability/limit/${limitToCurrentOrganization}` + queryParams
    );
  }

  confirmAgreements(): Observable<User> {
    return this.http.put<User>(`${this.environment.api_prefix}api/users/confirm-agreements`, {});
  }

  accounts(): Observable<Account[]> {
    return this.http.get<Account[]>(`${this.environment.api_prefix}api/users/accounts`);
  }

  switchAccount(accountId: string): Observable<void> {
    // Identify the initiating browser session: the backend echoes it in the account-switched SSE
    // broadcast so this tab can ignore its own event (it refreshes itself via the switch flow),
    // while the user's other sessions fully reload.
    const sessionId = localStorage.getItem(SESSION_ID);
    const options = sessionId ? {headers: {'X-Session-Id': sessionId}} : {};
    return this.http.post<void>(`${this.environment.api_prefix}api/users/accounts/${accountId}/switch`, {}, options);
  }

  acceptAccountInvite(accountId: string): Observable<Account[]> {
    return this.http.post<Account[]>(`${this.environment.api_prefix}api/users/accounts/${accountId}/accept`, {});
  }

  acceptAllAccountInvites(): Observable<Account[]> {
    return this.http.post<Account[]>(`${this.environment.api_prefix}api/users/accounts/accept-all`, {});
  }


  rejectAccountInvite(accountId: string): Observable<null> {
    return this.http.delete<null>(`${this.environment.api_prefix}api/users/accounts/${accountId}`);
  }

  getUsers(clientId: string = null, simple = false, includeGhostInvites = false): Observable<User[]> {
    const params = {};
    if (clientId) {
      params['client-id'] = clientId;
    }
    if (simple) {
      params['simple'] = true;
    }
    if (includeGhostInvites) {
      params['include-ghost-invites'] = true;
    }
    return this.http.get<User[]>(this.resourceUrl, { params });
  }

  addUser(userInvite: UserInvite, clientId?: string): Observable<User> {
    let clientQuery = '';
    if (clientId) {
      clientQuery += `?client-id=${clientId}`;
    }
    return this.http.post<User>(this.resourceUrl + clientQuery, userInvite);
  }

  bulkInviteUser(invite: BulkUserInvite): Observable<BulkUserInviteResponse> {
    return this.http.post<BulkUserInviteResponse>(`${this.resourceUrl}/bulk-invite`, invite);
  }

  updateUser(user: User, clientId?: string): Observable<User> {
    let clientQuery = '';
    if (clientId) {
      clientQuery += `?client-id=${clientId}`;
    }
    return this.http.put<User>(this.resourceUrl + clientQuery, user);
  }

  deleteUser(login: string, clientId?: string): Observable<void> {
    let clientQuery = '';
    if (clientId) {
      clientQuery += `?client-id=${clientId}`;
    }
    return this.http.delete<void>(`${this.resourceUrl}/${this.encodeLogin(login)}${clientQuery}`);
  }

  toggleDisableUser(login: string, clientId?: string): Observable<boolean> {
    let clientQuery = '';
    if (clientId) {
      clientQuery += `?client-id=${clientId}`;
    }
    return this.http.put<boolean>(`${this.resourceUrl}/${this.encodeLogin(login)}/toggle-disabled${clientQuery}`, {});
  }

  updateUserPositionGroup(login: string, permissionGroupId: string, clientId?: string): Observable<void> {
    return this.http.put<void>(`${this.resourceUrl}/${this.encodeLogin(login)}/permission-group`, {
      permission_group_id: permissionGroupId,
      client_id: clientId
    });
  }

  toggleAttorney(login: string): Observable<boolean> {
    return this.http.put<boolean>(`${this.resourceUrl}/${this.encodeLogin(login)}/attorney/toggle`, {});
  }

  updateUserPosition(login: string, role: string, clientId?: string): Observable<void> {
    return this.http.put<void>(`${this.resourceUrl}/${this.encodeLogin(login)}/switch-role`, { role, client_id: clientId });
  }

  resetUserPassword(login: string, force: boolean): Observable<void> {
    let forceQuery = '';
    if (force) {
      forceQuery += '?force=true';
    }
    return this.http.put<void>(`${this.resourceUrl}/${this.encodeLogin(login)}/reset-password${forceQuery}`, {});
  }

  /**
   * Set the password for a user - global admin only
   * @param login - The email address of the user
   * @param newPassword - The new password
   * @returns An observable of the void response
   */
  setUserPassword(login: string, newPassword: string): Observable<void> {
    return this.http.post<void>(`${this.environment.api_prefix}api/account/change-password`, {
      email: login,
      new_password: newPassword
    });
  }

  resendInvite(login: string, clientId?: string): Observable<void> {
    let clientQuery = '';
    if (clientId) {
      clientQuery += `?client-id=${clientId}`;
    }
    return this.http.put<void>(`${this.resourceUrl}/${this.encodeLogin(login)}/resend-invite${clientQuery}`, {});
  }

  /**
   * Cancel a pending invite for a user
   * Requirements: 2.3, 2.4
   * @param userId - The user ID of the pending invite
   * @param orgId - The organization ID
   * @returns An observable of the void response
   */
  cancelPendingInvite(userId: string, orgId: string): Observable<void> {
    return this.http.delete<void>(`${this.environment.api_prefix}api/organizations/${orgId}/users/${userId}/invite`);
  }

  getGridSettingsOptions(type: GridSettings['type'] = 'CASE'): Observable<GridSettings[]> {
    return this.http.get<GridSettings[]>(this.environment.api_prefix + 'api/users/grid-settings?type=' + type);
  }

  /** The case-list view this user last chose in the current org, or null for the built-in Default (or none). */
  getCaseViewPreference(): Observable<{view_id: string | null}> {
    return this.http.get<{view_id: string | null}>(this.environment.api_prefix + 'api/user/case-view-preference');
  }

  /** Remember the chosen case-list view; null (or 'Default') forgets it. */
  setCaseViewPreference(viewId: string | null): Observable<void> {
    return this.http.put<void>(this.environment.api_prefix + 'api/user/case-view-preference', {view_id: viewId});
  }

  getGridSettings(id: string, type?: GridSettings['type']): Observable<GridSettings> {
    const typeParam = type ? '?type=' + type : '';
    return this.http.get<GridSettings>(this.environment.api_prefix + 'api/users/grid-settings/' + id + typeParam);
  }

  createGridSettings(gridSettings: GridSettings): Observable<GridSettings[]> {
    return this.http.post<GridSettings[]>(this.environment.api_prefix + 'api/users/grid-settings', gridSettings);
  }

  updateGridSettings(gridSettings: GridSettings): Observable<GridSettings> {
    return this.http.put<GridSettings>(
      this.environment.api_prefix + 'api/users/grid-settings/' + gridSettings.id,
      gridSettings
    );
  }

  shareGridSettings(id: string, share: boolean): Observable<GridSettings[]> {
    return this.http.put<GridSettings[]>(
      this.environment.api_prefix + 'api/users/grid-settings/' + id + '/share?share=' + share,
      {}
    );
  }

  deleteGridSettings(id: string, type?: GridSettings['type']): Observable<GridSettings[]> {
    const typeParam = type ? '?type=' + type : '';
    return this.http.delete<GridSettings[]>(this.environment.api_prefix + 'api/users/grid-settings/' + id + typeParam);
  }

  // --- Per-user case-detail sidebar layout (the user layer of user → org → admin) ---

  /** The logged-in user's personal sidebar layout for a case type (null if none → org applies). */
  getCaseSidebarLayout(caseTypeId: string): Observable<SidebarProjection | null> {
    return this.http.get<SidebarProjection | null>(
      `${this.resourceUrl}/case-sidebar-layouts/${caseTypeId}`
    );
  }

  /** Save the logged-in user's personal sidebar layout for a case type. */
  updateCaseSidebarLayout(caseTypeId: string, projection: SidebarProjection): Observable<SidebarProjection> {
    return this.http.put<SidebarProjection>(
      `${this.resourceUrl}/case-sidebar-layouts/${caseTypeId}`,
      projection
    );
  }

  /** Clear the logged-in user's personal sidebar layout for a case type (falls back to org). */
  resetCaseSidebarLayout(caseTypeId: string): Observable<void> {
    return this.http.delete<void>(`${this.resourceUrl}/case-sidebar-layouts/${caseTypeId}`);
  }

  /** Case-type ids the logged-in user has personalized (for a settings management view). */
  listCaseSidebarLayoutTypes(): Observable<string[]> {
    return this.http.get<string[]>(`${this.resourceUrl}/case-sidebar-layouts`);
  }

  /**
   * Encode a login/email for use as a path segment so characters like '+' are not
   * misinterpreted (plus-addressed emails, e.g. user+tag@domain.com).
   */
  private encodeLogin(login: string): string {
    return encodeURIComponent(login);
  }
}

export interface BulkUserInvite {
  email: string;
  first_name: string;
  last_name: string;
  subject: string;
  message: string;
  permission_group_id: string;
  time_zone: string;
  client_ids: string[];
}

export interface BulkUserInviteResponse {
  success_count: number;
  skipped_client_ids: string[];
}
